import React from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { sanitizeText } from '@/utils/sanitizeText';

type UseDebouncedInputProps = {
  initialValue: string;
  onChange: (value: string) => void;
  debounceTime?: number;
};

type UseDebouncedInputReturn = {
  inputValue: string;
  handleChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  handleFocus: () => void;
  handleBlur: () => void;
  isInteracting: boolean;
};

export const useDebouncedInput = ({
  initialValue,
  onChange,
  debounceTime = 500,
}: UseDebouncedInputProps): UseDebouncedInputReturn => {
  const [inputValue, setInputValue] = React.useState(initialValue);
  const [isInteracting, setIsInteracting] = React.useState(false);

  const debouncedOnChange = useDebouncedCallback(onChange, debounceTime);

  // Block external updates during interaction to prevent cursor jumping
  React.useEffect(() => {
    if (!isInteracting && inputValue !== initialValue) {
      setInputValue(initialValue);
    }
  }, [initialValue, isInteracting]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = sanitizeText(event.target.value);
    setInputValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleFocus = () => {
    setIsInteracting(true);
  };

  const handleBlur = () => {
    setIsInteracting(false);
    debouncedOnChange.flush();
  };

  return {
    inputValue,
    handleChange,
    handleFocus,
    handleBlur,
    isInteracting,
  };
};
